import React, { ReactNode, useEffect, useState } from 'react';
import { MenuList, MenuItem, Typography, Box } from '@mui/material';
import { KeyboardArrowDown as DownIcon } from '@mui/icons-material';
import { Icons } from '../icons';
import { ColorUtils } from '../framework/utils';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { routeAtom } from '../atoms/route';

export type Route = {
    to: string
    label: string
    icon?: React.ReactNode
}

export type NavSection = {
    title: string;
    routes: Route[];
}

interface DrawerItemProps {
    index: number;
    item: Route;
    currentRoute: string;
    navigate: (route: Route) => void;
    icon?: ReactNode;
    compressed?: boolean;
}

function DrawerMenuItem(props: DrawerItemProps) {

    let { label, to } = props.item;
    const selected = props.currentRoute === to;

    return (
        <MenuItem
            key={props.index}
            divider={false}
            style={{
                paddingTop: '0.5em',
                paddingBottom: '0.5em',
                borderRight: selected ? `2px solid primary` : 0,
                backgroundColor: selected ? ColorUtils.toRGBA('#2c92ed', 0.2) : 'inherit'
            }}
            onClick={(e) => { props.navigate(props.item); }}
        >
            {props.icon
                ? <Box sx={{ ml: props.compressed ? 0 : -1, mr: 0.5 }} title={label}>{props.icon}</Box>
                : <Icons.Dot
                    size={selected ? 6 : 4}
                    style={{
                        marginRight: selected ? 8 : 9,
                        paddingLeft: selected ? 0 : 1,
                        fill: selected ? '#004c70' : 'rgba(0, 0, 0, 0.5)' // inherit'
                    }}
                />
            }
            {!props.compressed &&
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: selected ? 700 : 400,
                        color: selected ? '#004c70' : '#1d2d3d'
                    }}
                >{label}</Typography>
            }
            {/* <ListItemIcon style={{minWidth: '2rem'}}>
                // {icons[route] !== undefined ? icons[route] : <MenuIcon />}
                <MenuIcon />
            </ListItemIcon>
            <ListItemText primary={t(`views:${route}.index.title`)} style={{fontWeight: props.currentRoute === route ? 700 : 400}} /> */}
        </MenuItem>
    );
}

const useMenuGroupStyles = makeStyles({
    root: {
        // marginLeft: '-16px',
        // marginRight: '-16px'
    },
    header: {
        backgroundColor: '#eee',
        padding: '4px 16px',
        display: 'flex',
        alignItems: 'center',
        color: 'rgba(0, 0, 0, 0.8)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        minHeight: 32
    },
    label: {
        flex: '1 0 auto'
    },
    icon: {
        flex: '0 1 auto',
        color: 'rgba(0, 0, 0, 0.6)'
    },
    content: {
        // paddingLeft: '16px',
        // paddingRight: '16px'
    }
})

function MenuGroup({ title, children, compressed }: any) {

    const classes = useMenuGroupStyles();
    const [open, setOpen] = useState(true);

    return (
        <div className={classes.root}>
            <div className={classes.header} onClick={() => setOpen(!open)}>
                <div className={classes.label} title={title}><Typography variant="body1">{compressed ? `${title[0]}...` : title}</Typography></div>
                <div className={classes.icon}>{open ? null : <DownIcon />}</div>
            </div>
            <div className={classes.content}>
                {open ? children : null}
            </div>
        </div>
    );
}


type NavProps = {
    sections: NavSection[],
    compressed?: boolean,
}

export const NavView = ({ sections, compressed }: NavProps) => {
    const navigate = useNavigate();
    const location = useLocation()
    // const { actions } = useAppActions();
    const [currentRoute, setCurrentRoute] = useRecoilState<string>(routeAtom)
    useEffect(() => {
        if (location.pathname === "/") {
            setCurrentRoute("/")
        } else {
            const routes = sections.reduce<Route[]>((p, c) => ([...p, ...c.routes]), [])
            const rr = routes.find(r => location.pathname.startsWith(r.to))
            rr && rr.to !== currentRoute && setCurrentRoute(rr.to)
        }
    }, [location, currentRoute, sections, setCurrentRoute])

    const handler = {
        navigateTo: (route: Route) => {
            navigate(route.to)
            setCurrentRoute(route.to)
        }
    }

    return (
        <MenuList style={{ padding: 0, flex: 1 }} variant="selectedMenu">
            {sections.map((item, index) => (
                <MenuGroup
                    key={index}
                    title={item.title}
                    compressed={compressed}
                >
                    {item.routes.map((route, routeIndex) => (
                        <DrawerMenuItem
                            key={routeIndex}
                            compressed={compressed}
                            index={routeIndex}
                            item={route}
                            currentRoute={currentRoute}
                            navigate={handler.navigateTo}
                            icon={route.icon ?? route.icon}
                        />
                    ))}
                </MenuGroup>
            ))}
        </MenuList>
    );
}